import { SELECTED_AUDIO_INPUT_KEY, SELECTED_VIDEO_INPUT_KEY } from '../../../constants';
import { getDeviceInfo, isPermissionDenied } from '../../../utils';
import { useCallback, useState } from 'react';
import AgoraRTC, { ILocalAudioTrack, ILocalVideoTrack } from 'agora-rtc-sdk-ng';
import { useAppState } from '../../../state';

export default function useLocalTracks() {
  useAppState();
  const [audioTrack, setAudioTrack] = useState<ILocalAudioTrack | undefined>();
  const [videoTrack, setVideoTrack] = useState<ILocalVideoTrack | undefined>();
  const [screenTrack, setScreenTrack] = useState<ILocalVideoTrack | undefined>();
  const [isAcquiringLocalTracks, setIsAcquiringLocalTracks] = useState(false);

  const getLocalVideoTrack = useCallback(async () => {
    const selectedVideoDeviceId = window.localStorage.getItem(SELECTED_VIDEO_INPUT_KEY);
    const { videoInputDevices } = await getDeviceInfo();
    const hasSelectedVideoDevice = videoInputDevices.some(
      device => selectedVideoDeviceId && device.deviceId === selectedVideoDeviceId
    );
    const newTrack = await AgoraRTC.createCameraVideoTrack({
      encoderConfig: { width: 1280, height: 720, frameRate: 24 },
      ...(hasSelectedVideoDevice && { cameraId: selectedVideoDeviceId ?? undefined }),
    });
    (newTrack as any).trackName = `camera-${Date.now()}`;
    setVideoTrack(newTrack);
    return newTrack;
  }, []);

  const removeLocalAudioTrack = useCallback(() => {
    if (audioTrack) {
      audioTrack.close();
      setAudioTrack(undefined);
    }
  }, [audioTrack]);

  const removeLocalVideoTrack = useCallback(() => {
    if (videoTrack) {
      videoTrack.close();
      setVideoTrack(undefined);
    }
  }, [videoTrack]);

  const setLocalScreenTrack = useCallback((track: ILocalVideoTrack | undefined) => {
    setScreenTrack(prev => {
      if (prev && prev !== track) {
        try {
          prev.close();
        } catch {
          /* ignore */
        }
      }
      return track;
    });
  }, []);

  const removeLocalScreenTrack = useCallback(() => {
    if (screenTrack) {
      try {
        screenTrack.close();
      } catch {
        /* ignore */
      }
      setScreenTrack(undefined);
    }
  }, [screenTrack]);

  const getAudioAndVideoTracks = useCallback(async () => {
    const { audioInputDevices, videoInputDevices, hasAudioInputDevices, hasVideoInputDevices } = await getDeviceInfo();
    if (!hasAudioInputDevices && !hasVideoInputDevices) return Promise.resolve();
    if (isAcquiringLocalTracks || audioTrack || videoTrack) return Promise.resolve();

    setIsAcquiringLocalTracks(true);
    const selectedAudioDeviceId = window.localStorage.getItem(SELECTED_AUDIO_INPUT_KEY);
    const selectedVideoDeviceId = window.localStorage.getItem(SELECTED_VIDEO_INPUT_KEY);
    const hasSelectedAudioDevice = audioInputDevices.some(
      device => selectedAudioDeviceId && device.deviceId === selectedAudioDeviceId
    );
    const hasSelectedVideoDevice = videoInputDevices.some(
      device => selectedVideoDeviceId && device.deviceId === selectedVideoDeviceId
    );
    const isCameraPermissionDenied = await isPermissionDenied('camera');
    const isMicrophonePermissionDenied = await isPermissionDenied('microphone');
    const shouldAcquireVideo = hasVideoInputDevices && !isCameraPermissionDenied;
    const shouldAcquireAudio = hasAudioInputDevices && !isMicrophonePermissionDenied;

    try {
      const tracks: (ILocalAudioTrack | ILocalVideoTrack)[] = [];
      if (shouldAcquireAudio) {
        const a = await AgoraRTC.createMicrophoneAudioTrack({
          ...(hasSelectedAudioDevice && { microphoneId: selectedAudioDeviceId ?? undefined }),
        });
        (a as any).trackName = 'audio';
        tracks.push(a);
        setAudioTrack(a);
      }
      if (shouldAcquireVideo) {
        const v = await AgoraRTC.createCameraVideoTrack({
          encoderConfig: { width: 1280, height: 720, frameRate: 24 },
          ...(hasSelectedVideoDevice && { cameraId: selectedVideoDeviceId ?? undefined }),
        });
        (v as any).trackName = `camera-${Date.now()}`;
        tracks.push(v);
        setVideoTrack(v);
        window.localStorage.setItem(SELECTED_VIDEO_INPUT_KEY, v.getTrackLabel() || '');
      }
      if (isCameraPermissionDenied && isMicrophonePermissionDenied) {
        const err = new Error();
        err.name = 'NotAllowedError';
        throw err;
      }
      if (isCameraPermissionDenied) throw new Error('CameraPermissionsDenied');
      if (isMicrophonePermissionDenied) throw new Error('MicrophonePermissionsDenied');
    } finally {
      setIsAcquiringLocalTracks(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- setIsKrispEnabled/setIsKrispInstalled are stable
  }, [audioTrack, videoTrack, isAcquiringLocalTracks]);

  const localTracks = [audioTrack, videoTrack, screenTrack].filter(Boolean) as (ILocalAudioTrack | ILocalVideoTrack)[];

  return {
    localTracks,
    getLocalVideoTrack,
    isAcquiringLocalTracks,
    removeLocalAudioTrack,
    removeLocalVideoTrack,
    setLocalScreenTrack,
    removeLocalScreenTrack,
    getAudioAndVideoTracks,
  };
}
